import { getCollectionHref } from "@/app/navigation/collection"
import { getCanonicalCollectionPath } from "@/app/navigation/collection/collection-path"
import type { LaunchedCollectionPublic } from "@anaconda/core/collection-launcher/types"

/**
 * Configuration for blockchain-specific branded sliders
 * These sliders appear on specific blockchain routes and can have custom videos, images, and content
 */

export interface BrandSliderConfig {
  name: string
  description: string
  mobileName?: string
  mobileDescription?: string
  slug: string
  supportedBlockchains: string[]
  backgroundType?: "image" | "video"
  video: {
    desktop: string
    mobile: string
  }
  image: string
  /** First frame of a video `image`, shown until its preview plays */
  imagePoster?: string
  isActive: boolean
  showNewBrandLabel?: boolean
  /** Optional custom CTA text - if not provided, defaults to Explore {name} */
  ctaButtonText?: string
  /** Optional custom URL - if not provided, defaults to /{slug} */
  url?: string
  /** Open the CTA URL in a new browser tab */
  openCtaInNewTab?: boolean
  /**
   * Stills the carousel thumbnail cuts between while its slide shows, as the
   * configured slides' square preview videos do with three PFPs
   */
  previewImages?: string[]
  /**
   * Set on a launched collection's slide: what the homepage layout knows it
   * by, since its slug can change
   */
  collectionId?: string
}

/**
 * Hardcoded brand slider configurations
 * Add new brand sliders here
 */
export const BRAND_SLIDERS: BrandSliderConfig[] = [
  {
    name: "OnChainHoodies",
    description: "Step into the world of OnChainHoodies",
    slug: "robinhood/collections/onchainhoodies",
    supportedBlockchains: ["ALL", "ROBINHOOD"],
    backgroundType: "image",
    video: {
      desktop: "/images/collections/onchainhoodies/banner.webp",
      mobile: "/images/collections/onchainhoodies/banner.webp",
    },
    image: "/images/collections/onchainhoodies/square.mp4",
    imagePoster: "/images/collections/onchainhoodies/square-poster.webp",
    isActive: true,
    ctaButtonText: "Trade Collection",
  },
  {
    name: "MonkeyHood",
    description: "Step into the world of MonkeyHood",
    slug: "robinhood/collections/monkeyhood",
    supportedBlockchains: ["ALL", "ROBINHOOD"],
    backgroundType: "image",
    video: {
      desktop: "/images/collections/monkeyhood/monkeyhood.png",
      mobile: "/images/collections/monkeyhood/monkeyhood.png",
    },
    image: "/images/collections/monkeyhood/monkeyhood.mp4",
    imagePoster: "/images/collections/monkeyhood/monkeyhood-poster.webp",
    isActive: true,
    ctaButtonText: "Trade Collection",
  },
  {
    name: "Trencher Art",
    description: "Step into the world of Trencher Art",
    slug: "solana/collections/trencher",
    supportedBlockchains: ["ALL", "SOLANA"],
    backgroundType: "image",
    video: {
      desktop: "/images/banners/trencher-banner.webp",
      mobile: "/images/banners/trencher-banner.webp",
    },
    image: "/images/banners/trencher-square.mp4",
    imagePoster: "/images/banners/trencher-square-poster.webp",
    isActive: true,
    ctaButtonText: "Trade Collection",
  },
  {
    name: "Bored Ape Solana Club",
    description: "Step into the world of Bored Ape Solana Club",
    slug: "solana/collections/bored-ape-solana-club",
    supportedBlockchains: ["ALL", "SOLANA"],
    backgroundType: "image",
    video: {
      desktop: "/images/banners/basc-banner.webp",
      mobile: "/images/banners/basc-banner.webp",
    },
    image: "/images/banners/basc-square.webm",
    imagePoster: "/images/banners/basc-square-poster.webp",
    isActive: true,
    ctaButtonText: "Trade Collection",
  },
  {
    name: "y00ts",
    description: "Step into the world of y00ts",
    slug: "solana/collections/y00ts",
    supportedBlockchains: ["ALL", "SOLANA"],
    backgroundType: "video",
    video: {
      desktop: "/images/banners/y00ts-banner.mp4",
      mobile: "/images/banners/y00ts-banner.mp4",
    },
    image: "/images/banners/y00ts-square.mp4",
    imagePoster: "/images/banners/y00ts-square-poster.webp",
    isActive: true,
    ctaButtonText: "Trade Collection",
  },
  {
    name: "Bobos of War",
    description: "Step into the world of Bobos of War",
    slug: "solana/collections/bobos-of-war",
    supportedBlockchains: ["SOLANA"],
    backgroundType: "video",
    video: {
      desktop: "/images/banners/bobos-of-war-banner.webm",
      mobile: "/images/banners/bobos-of-war-banner.webm",
    },
    image: "/images/banners/bobos-of-war-square.webm",
    imagePoster: "/images/banners/bobos-of-war-square-poster.webp",
    isActive: true,
    ctaButtonText: "Trade Collection",
  },
  {
    name: "Solana Monke Business Gen 3",
    description: "Step into the world of Solana Monke Business Gen 3",
    slug: "solana/collections/smb-gen3",
    supportedBlockchains: ["ALL", "SOLANA"],
    backgroundType: "image",
    video: {
      desktop: "/images/banners/smb-banner.webp",
      mobile: "/images/banners/smb-banner.webp",
    },
    image: "/images/banners/smb-square.webp",
    isActive: true,
    ctaButtonText: "Trade Collection",
  },
  {
    name: "Mad Lads",
    description: "Step into the world of Mad Lads",
    slug: "solana/collections/mad-lads",
    supportedBlockchains: ["ALL", "SOLANA"],
    backgroundType: "image",
    video: {
      desktop: "/images/banners/mad-lads-banner.webp",
      mobile: "/images/banners/mad-lads-banner.webp",
    },
    image: "/images/banners/mad-lads-square.webp",
    isActive: true,
    ctaButtonText: "Trade Collection",
  },
  {
    name: "Claynosaurz",
    description: "Step into the world of Claynosaurz",
    slug: "solana/collections/claynosaurz",
    supportedBlockchains: ["ALL", "SOLANA"],
    backgroundType: "image",
    video: {
      desktop: "/images/banners/claynosaurz-banner.webp",
      mobile: "/images/banners/claynosaurz-banner.webp",
    },
    image: "/images/banners/claynosaurz-square.webp",
    isActive: true,
    ctaButtonText: "Trade Collection",
  },
  {
    name: "Gacha Station is Live",
    description:
      "Crack a Pack and discover live pulls from our Rarible Gacha Station",
    slug: "gacha",
    url: "https://rwa.rarible.com/gacha",
    supportedBlockchains: ["ALL"],
    backgroundType: "image",
    video: {
      desktop: "/images/gacha-live.webp",
      mobile: "/images/gacha-live.webp",
    },
    image: "/images/banners/gacha-live-square.webm",
    imagePoster: "/images/banners/gacha-live-square-poster.webp",
    isActive: true,
    ctaButtonText: "Explore Rarible Gacha",
  },
  {
    name: "VeeFriends",
    description:
      "VeeFriends is an entertainment company blending stories, games, events, collectibles, and tech to build a community of kindness and accountability.",
    slug: "veefriends",
    supportedBlockchains: ["ETHEREUM", "BASE", "ALL"],
    video: {
      desktop: "/images/banners/veefriends-banner.mp4",
      mobile: "/images/banners/veefriends-banner.mp4",
    },
    image: "/images/banners/veefriends-square.webp",
    isActive: true,
  },
]

export function isBrandSliderPreviewVideo(image: string) {
  return /\.(?:mp4|webm)(?:$|[?#])/i.test(image)
}

export function getBrandSliderCtaText(slider: BrandSliderConfig) {
  return slider.ctaButtonText?.trim() || `Explore ${slider.name}`
}

/**
 * Get brand sliders for a specific blockchain
 * @param blockchain - The blockchain to filter by (e.g., "ETHEREUM", "POLYGON", "ALL")
 * @returns Array of active brand sliders for the specified blockchain
 */
export function getBrandSlidersForBlockchain(
  blockchain: string,
): BrandSliderConfig[] {
  const normalizedBlockchain = blockchain.toUpperCase()

  return BRAND_SLIDERS.filter(
    (slider) =>
      slider.isActive &&
      slider.supportedBlockchains
        .map((bc) => bc.toUpperCase())
        .includes(normalizedBlockchain),
  )
}

/**
 * Get a specific brand slider by slug
 * @param slug - The slider slug
 * @returns Brand slider config or undefined if not found
 */
export function getBrandSliderBySlug(
  slug: string,
): BrandSliderConfig | undefined {
  return BRAND_SLIDERS.find((slider) => slider.isActive && slider.slug === slug)
}

/**
 * The homepage slide of a collection published from the admin Collection
 * Launcher, on its own blockchain and on All: its 21:5 banner behind, and a
 * carousel thumbnail that cycles its three PFPs, like the configured slides'
 * preview videos.
 */
export function launchedCollectionToBrandSlider(
  launch: LaunchedCollectionPublic,
): BrandSliderConfig {
  const href = getCollectionHref(launch.collectionId)

  return {
    name: launch.name,
    description: launch.description || `Step into the world of ${launch.name}`,
    slug: href.slice(1),
    supportedBlockchains: ["ALL", launch.blockchain],
    backgroundType: "image",
    video: { desktop: launch.banner, mobile: launch.banner },
    image: launch.pfps[0] ?? launch.banner,
    previewImages: launch.pfps,
    isActive: true,
    ctaButtonText: "Trade Collection",
    url: href,
    collectionId: launch.collectionId,
  }
}

/**
 * Where a slide's CTA goes, compared without case (EVM addresses vary in it)
 * and by slug, whether the slide names its collection by slug or by address.
 */
function getBrandSliderHref(slider: BrandSliderConfig) {
  const href = slider.url ?? `/${slider.slug}`
  return (getCanonicalCollectionPath(href)?.path ?? href).toLowerCase()
}

/**
 * Launched collections' slides lead, newest first. A configured slide for the
 * same collection page gives way to the launched one.
 */
export function mergeBrandSliders(
  launched: BrandSliderConfig[],
  configured: BrandSliderConfig[],
): BrandSliderConfig[] {
  const hrefs = new Set(launched.map(getBrandSliderHref))
  return [
    ...launched,
    ...configured.filter((slider) => !hrefs.has(getBrandSliderHref(slider))),
  ]
}
